"use client";

import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqCategories = [
  {
    label: "Business Setup Basics",
    faqs: [
      {
        question: "How does Dubai's economy support new businesses?",
        answer:
          "Dubai offers low corporate taxes, world-class digital infrastructure, strategic global connectivity, and a strong legal framework designed to attract international ventures. Multiple free zones provide industry-specific ecosystems, while mainland setup enables direct access to the local UAE market. Dubai remains one of the most attractive business hubs globally due to its investor-friendly policies.",
      },
      {
        question: "What are the steps to set up a business in Dubai?",
        answer: (
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Define your business idea and plan</li>
            <li>Choose your legal structure (Mainland, Free Zone, or Offshore)</li>
            <li>Register a compliant trade name</li>
            <li>Apply for your business licence</li>
            <li>Submit all required documentation</li>
            <li>Secure an office or virtual desk</li>
            <li>Open a corporate bank account</li>
            <li>Process owner and employee visas</li>
            <li>Launch operations</li>
          </ol>
        ),
      },
      {
        question: "How do I choose the best location for my business?",
        answer:
          "Consider your business activity type (some free zones are sector-specific), your target market (mainland for local UAE trade, free zone for export or international focus), infrastructure needs, budget, and licensing requirements. We recommend a professional consultation to get a recommendation tailored to your specific goals.",
      },
      {
        question: "What are the differences between Mainland and Free Zone?",
        answer: (
          <div className="space-y-3 text-gray-600">
            <p>
              <strong className="text-[#0A0A0A]">Mainland:</strong> Up to 100% foreign ownership in most sectors (per updated UAE Company Law). Direct access to the full UAE market, eligibility for government contracts, physical office generally required.
            </p>
            <p>
              <strong className="text-[#0A0A0A]">Free Zone:</strong> 100% foreign ownership without a local partner. Tax incentives and simplified registration. Free zone companies generally cannot trade directly with mainland UAE customers unless through a local distributor or mainland branch.
            </p>
          </div>
        ),
      },
      {
        question: "What types of business licences are available in Dubai?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>
              <strong className="text-[#0A0A0A]">Commercial Licence</strong> For trading activities
            </li>
            <li>
              <strong className="text-[#0A0A0A]">Professional Licence</strong> For service-based businesses and consultancies
            </li>
            <li>
              <strong className="text-[#0A0A0A]">Industrial Licence</strong> For manufacturing activities
            </li>
            <li>Free zones also offer specialized sector-specific licence categories</li>
          </ul>
        ),
      },
    ],
  },
  {
    label: "Legal & Regulatory",
    faqs: [
      {
        question: "What are the legal and regulatory requirements for business setup?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Define permitted business activities</li>
            <li>Select a legal structure and licensing jurisdiction</li>
            <li>Apply for and obtain a trade licence</li>
            <li>Register Ultimate Beneficial Owners (UBO)</li>
            <li>Open a corporate bank account</li>
            <li>Process visas and comply with labour and immigration rules</li>
            <li>Adhere to UAE naming and legal conventions</li>
          </ul>
        ),
      },
      {
        question: "How do Local Service Agent (LSA) and local sponsor arrangements work?",
        answer: (
          <div className="space-y-3 text-gray-600">
            <p>
              <strong className="text-[#0A0A0A]">LSA (Local Service Agent):</strong> Required for certain professional setups or branches. The agent assists with government processes but holds no equity in the company.
            </p>
            <p>
              <strong className="text-[#0A0A0A]">Local Sponsor:</strong> Historically required for mainland LLCs. Updated UAE laws now permit 100% foreign ownership in many sectors, though some strategic activities may still require a UAE national partner.
            </p>
          </div>
        ),
      },
      {
        question: "What legal structures are available for business setup in Dubai?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Limited Liability Company (LLC)</li>
            <li>Sole Proprietorship</li>
            <li>Civil Company (for licensed professionals)</li>
            <li>Private Shareholder Company</li>
            <li>Public Shareholding Company</li>
            <li>Branch & Representative Offices</li>
            <li>Free Zone Company</li>
            <li>Offshore Company</li>
          </ul>
        ),
      },
      {
        question: "Can foreigners own 100% of a UAE business?",
        answer:
          "Yes UAE law now allows 100% foreign ownership in many mainland and free zone companies, subject to activity-specific rules. Exceptions remain in sensitive sectors such as oil & gas and banking. Our consultants will confirm eligibility based on your specific business activity.",
      },
      {
        question: "How are legal documents managed and attested?",
        answer:
          "Documents must be submitted to the relevant UAE authorities, verified, and kept current to maintain your business licence. Attestation ensures documents meet legal authenticity requirements set by the relevant licensing authority. We manage this process on your behalf.",
      },
    ],
  },
  {
    label: "Operations & Banking",
    faqs: [
      {
        question: "How does business licence renewal work?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Ensure your tenancy contract and all documentation are valid</li>
            <li>Submit renewal forms with updated owner and partner details</li>
            <li>Pay renewal fees through DED (mainland) or the relevant free zone authority</li>
          </ul>
        ),
      },
      {
        question: "Do I need a physical office to start a business in Dubai?",
        answer:
          "Mainland companies must meet physical office requirements.. Free zones often allow flexi desk or virtual office packages as legal premises, depending on the number of visas required and the nature of your business activity.",
      },
      {
        question: "How can I open a corporate bank account in Dubai?",
        answer: (
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Select a suitable UAE bank for your business type</li>
            <li>Schedule an initial meeting with the bank</li>
            <li>Submit all required documentation (licence, MOA, passport copies, etc.)</li>
            <li>Complete the bank's due diligence process</li>
            <li>Receive approval typically within 2–4 weeks</li>
          </ol>
        ),
      },
      {
        question: "Are there share capital requirements for business setup?",
        answer:
          "Share capital requirements vary by free zone, legal structure, and business activity. Some free zones require a minimum share capital for specific licences while others do not. We advise reviewing requirements with your chosen free zone authority our team can guide you through this.",
      },
    ],
  },
  {
    label: "Visas & Immigration",
    faqs: [
      {
        question: "What is the process for obtaining employment and residence visas?",
        answer: (
          <ol className="list-decimal list-inside space-y-2 text-gray-600">
            <li>Apply for an entry permit or work permit</li>
            <li>Complete required medical fitness tests</li>
            <li>Apply for Emirates ID registration</li>
            <li>Finalise residence visa stamping in passport</li>
          </ol>
        ),
      },
      {
        question: "What are typical visa processing times and fees?",
        answer:
          "Visa processing typically takes 2–4 weeks depending on the completeness of submitted documentation and the type of visa. Fees vary by visa category and issuing authority. We provide a transparent fee breakdown upfront so there are no surprises.",
      },
      {
        question: "What visa types does AWS Businessmen handle?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Investor / owner visas</li>
            <li>Employment visas for staff</li>
            <li>Family and dependent visas</li>
            <li>UAE tourist visas (30-day and 90-day, single and multiple entry)</li>
            <li>Long-term Golden Visa (where eligible)</li>
          </ul>
        ),
      },
    ],
  },
  {
    label: "Free Zones",
    faqs: [
      {
        question: "How do UAE free zones differ from one another?",
        answer: (
          <div className="space-y-3 text-gray-600">
            <p>Each free zone caters to specific industries. Key examples:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-[#0A0A0A]">JAFZA</strong>Ideal for logistics and manufacturing; near ports and airports
              </li>
              <li>
                <strong className="text-[#0A0A0A]">DWTC</strong>Central location in Dubai's main business district
              </li>
              <li>
                <strong className="text-[#0A0A0A]">RAKEZ</strong>Wide range of sectors; cost-effective solutions
              </li>
              <li>
                <strong className="text-[#0A0A0A]">DUQE</strong>Unique environment on the Queen Elizabeth Cruise Ship
              </li>
              <li>
                <strong className="text-[#0A0A0A]">Creative City</strong>Focused on media, communications, and creative industries
              </li>
            </ul>
            <p>Selection should align with your business model, sector, and operational needs.</p>
          </div>
        ),
      },
      {
        question: "What are the restrictions on foreign company branches in Dubai?",
        answer:
          "Branches are extensions of the parent company and are not separate legal entities. Representative offices are prohibited from generating local profits. Access to the UAE local market may require compliance with UAE commercial regulations or the appointment of a local agent.",
      },
      {
        question: "How does Dubai's digital infrastructure support business registration?",
        answer:
          "Dubai offers advanced government digital platforms for company registration, licence applications, and compliance filings significantly reducing paperwork and turnaround times. UAE government portals are digitally integrated across most free zones and mainland authorities.",
      },
      {
        question: "How can business setup consultants assist me?",
        answer: (
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Strategic advice on the right structure and business activities</li>
            <li>Document preparation and regulatory guidance</li>
            <li>Bank account opening assistance</li>
            <li>Visa and government filing support</li>
            <li>Ongoing compliance monitoring and renewal management</li>
          </ul>
        ),
      },
    ],
  },
];

export const Faq = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);

  const currentFaqs = useMemo(() => {
    return faqCategories?.[activeCategory]?.faqs || [];
  }, [activeCategory]);

  const toggleFAQ = useCallback((index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  const handleCategory = useCallback((i) => {
    setActiveCategory(i);
    setActiveIndex(null);
  }, []);

  return (
    <section className="relative bg-[#FAFAFA] py-12 sm:py-16 lg:py-20 xl:py-24 px-4 overflow-hidden">

      <div className="main-container">

        {/* HEADER */}
        <div className="text-left md:text-center mb-14 md:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-3">
            FAQ
          </p>

          <h1 
            className="font-bold text-[#000000]"
            style={{ 
              fontSize: "clamp(28px, 5vw, 48px)",
              fontWeight: "700",
              lineHeight: "1.3"
            }}
          >
            Frequently Asked <span className="text-[#000000]">Questions</span>
          </h1>

          <p 
            className="text-gray-500 mt-5 max-w-4xl md:mx-auto"
            style={{ 
              fontSize: "clamp(14px, 1.2vw, 16px)",
              lineHeight: "1.6"
            }}
          >
            Everything you need to know about setting up and growing your business in the UAE.
          </p>
        </div>

        {/* CATEGORY TABS */}
        <div className="flex flex-wrap justify-start md:justify-center gap-2 md:gap-3 mb-10">
          {faqCategories.map((cat, i) => (
            <button
              key={i}
              onClick={() => handleCategory(i)}
              className={`px-4 md:px-6 py-2.5 md:py-3 rounded-full text-[10px] md:text-sm font-semibold transition-all duration-300 ${activeCategory === i
                  ? "bg-[#B8893A] text-white shadow-md"
                  : "bg-[#fafafa] border border-gray-200 text-gray-600 hover:border-[#B8893A] hover:text-[#B8893A]"
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ LIST */}
        <div className="space-y-3">
          {currentFaqs.map((faq, index) => (
            <motion.div
              key={index}
              layout
              className="bg-[#fafafa] border border-gray-100 rounded-2xl shadow-sm overflow-hidden transition-all"
            >

              {/* QUESTION */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between gap-4 px-5 md:px-7 py-4 md:py-5 text-left"
              >
                <span 
                  className="font-semibold text-black leading-snug"
                  style={{ 
                    fontSize: "clamp(14px, 1.2vw, 16px)"
                  }}
                >
                  {faq.question}
                </span>

                <motion.div
                  animate={{ rotate: activeIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"
                >
                  <ChevronDown size={18} className="text-[#B8893A]" />
                </motion.div>
              </button>

              {/* ANSWER */}
              <AnimatePresence initial={false}>
                {activeIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div 
                      className="px-5 md:px-7 pb-5 md:pb-6 text-gray-600 border-t border-gray-100"
                      style={{ 
                        fontSize: "clamp(14px, 1.2vw, 16px)",
                        lineHeight: "1.6"
                      }}
                    >
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </motion.div>
          ))}
        </div>

      </div>

      {/* BACKGROUND GLOW */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10"></div>
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#B8893A]/5 blur-3xl rounded-full -z-10"></div>

    </section>
  );
};
